import { DatePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  OnInit
} from '@angular/core';
import { IntlDurationPipe } from '@app/shared/pipes/intl-duration.pipe';
import { AbsenceReportDTO } from '@app/shared/services/dtos/reports/absence-report.dto';
import { TimeReportPanelFiltersService } from '@app/shared/services/time-report-panel-filters.service';
import { AnyTimeReport } from '@app/shared/types/models/reports/any-time-report';
import {
  SelectedTimeReportTypeEnum,
  TimeReportSessionTypeEnum
} from '@app/shared/types/models/reports/time-report-panel.model';
import { WorkTimeReport } from '@app/shared/types/models/reports/work-time-report';
import { User } from '@app/shared/types/models/user/user.model';
import { PROFFEO_CHART_COLORS, resolveProffeoChartColor } from '@app/shared/ui/tokens/chart.tokens';
import {
  CHART_ABSENCE_STACK,
  CHART_WORK_TIME_STACK,
  ChartHoursTypeSeriesKey,
  TimeReportMixedChartView
} from '@app/shared/utils/time-report-chart.aggregation';
import { Chart, ChartDataset, registerables } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { HOURS_TYPE_COLORS } from './constants/chart-scale.constants';
import { AbsenceChartDataFormatterService } from './services/absence-chart-data-formatter.service';
import { ChartConfigService } from './services/chart-config.service';
import { ChartDataFormatterService } from './services/chart-data-formatter.service';
import { TimeReportCalculatorService } from './services/time-report-calculator.service';

Chart.register(...registerables);
Chart.register(annotationPlugin);

@Component({
  selector: 'app-time-report-panel-chart',
  templateUrl: './time-report-panel-chart.component.html',
  styleUrls: ['./time-report-panel-chart.component.scss'],
  imports: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    IntlDurationPipe,
    DatePipe,
    ChartConfigService,
    ChartDataFormatterService,
    AbsenceChartDataFormatterService,
    TimeReportCalculatorService
  ]
})
export class TimeReportPanelChartComponent implements OnInit, AfterViewInit {
  private readonly chartConfigService = inject(ChartConfigService);
  private readonly chartDataFormatterService = inject(ChartDataFormatterService);
  private readonly absenceChartDataFormatterService = inject(AbsenceChartDataFormatterService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private selectedFilters = inject(TimeReportPanelFiltersService).appliedFilters;
  private chart: Chart;

  public filteredWorkTimeReports = input.required<AnyTimeReport[][]>();
  public flattenedWorkTimeReports = input.required<AnyTimeReport[]>();
  public selectedMode = input.required<SelectedTimeReportTypeEnum>();
  public availableUsers = input.required<User[]>();
  public selectedYear = input.required<number>();
  public selectedMonth = input.required<number>();
  public selectedWeek = input.required<number>();
  public selectedDay = input.required<number>();
  public absenceReports = input<boolean>(false);
  public mixedChartView = input<TimeReportMixedChartView | undefined>(undefined);

  public ngOnInit(): void {
    this.initChart();
  }

  public ngAfterViewInit(): void {
    effect(
      () => {
        this.filteredWorkTimeReports();
        this.flattenedWorkTimeReports();
        this.absenceReports();
        this.mixedChartView();

        this.updateChartData();
        this.updateChartScale();
      },
      { injector: this.injector }
    );
  }

  private updateChartData(): void {
    if (!this.chart) return;

    const mixedView = this.mixedChartView();
    if (mixedView) {
      this.chart.data = this.toMixedChartJsData(mixedView);
      this.chart.update();
      return;
    }

    const mode = this.selectedMode();
    const userCount = this.getUserCount();
    const filteredReports = this.filteredWorkTimeReports();
    const isAbsence = this.absenceReports();

    if (isAbsence) {
      if (mode === SelectedTimeReportTypeEnum.RANGE) {
        this.chart.data = this.absenceChartDataFormatterService.getRangeChartData(
          filteredReports as unknown as AbsenceReportDTO[][]
        );
      } else {
        this.chart.data = this.absenceChartDataFormatterService.getChartDataByMode(
          mode,
          filteredReports as unknown as AbsenceReportDTO[][],
          this.selectedYear(),
          this.selectedMonth(),
          this.selectedWeek(),
          this.selectedDay()
        );
      }
    } else {
      if (mode === SelectedTimeReportTypeEnum.RANGE) {
        this.chart.data = this.chartDataFormatterService.getRangeChartData(
          filteredReports as WorkTimeReport[][],
          userCount
        );
      } else {
        this.chart.data = this.chartDataFormatterService.getChartDataByMode(
          mode,
          filteredReports as WorkTimeReport[][],
          this.selectedYear(),
          this.selectedMonth(),
          this.selectedWeek(),
          this.selectedDay()
        );
      }
    }

    this.chart.update();
  }

  private toMixedChartJsData(view: TimeReportMixedChartView): {
    labels: string[];
    datasets: ChartDataset<'bar', number[]>[];
  } {
    const datasets: ChartDataset<'bar', number[]>[] = view.workTimeSeries.map(series => ({
      label: series.label,
      data: series.hours,
      backgroundColor: resolveHoursTypeChartColor(series.hoursType),
      barPercentage: 0.8,
      categoryPercentage: 0.8,
      stack: CHART_WORK_TIME_STACK
    }));
    if (view.includeAbsence) {
      datasets.push({
        label: view.absenceSeriesLabel,
        data: view.absenceHours,
        backgroundColor: resolveProffeoChartColor(PROFFEO_CHART_COLORS.holiday),
        barPercentage: 0.8,
        categoryPercentage: 0.8,
        stack: CHART_ABSENCE_STACK
      });
    }
    return { labels: view.labels, datasets };
  }

  private updateChartScale(): void {
    if (!this.chart) return;

    const maxDataValue = this.chartConfigService.getMaxStackedValue(this.chart);
    const mode = this.selectedMode();
    const userCount = this.getUserCount();

    const newMaxYScale = this.chartConfigService.calculateMaxYScale(maxDataValue, mode, userCount);

    this.chart.options.scales!.y!.max = newMaxYScale;
    this.chart.update();
  }

  private initChart(): void {
    if (this.chart) {
      this.chart.destroy();
    }

    const ctx = document.getElementById('timeReportPanelChart') as HTMLCanvasElement;
    const config = this.chartConfigService.createBarChartConfig();

    this.chart = new Chart(ctx, config);
  }

  private getUserCount(): number {
    const filters = this.selectedFilters();
    const users = this.availableUsers();
    return filters?.users?.length || users.length;
  }
}

function resolveHoursTypeChartColor(hoursType: ChartHoursTypeSeriesKey): string {
  switch (hoursType) {
    case TimeReportSessionTypeEnum.STANDARD:
      return resolveProffeoChartColor(HOURS_TYPE_COLORS.STANDARD);
    case TimeReportSessionTypeEnum.BANK_PLUS:
      return resolveProffeoChartColor(HOURS_TYPE_COLORS.BANK_PLUS);
    case TimeReportSessionTypeEnum.OVERTIME_50:
      return resolveProffeoChartColor(HOURS_TYPE_COLORS.OVERTIME_50);
    case TimeReportSessionTypeEnum.OVERTIME_100:
      return resolveProffeoChartColor(HOURS_TYPE_COLORS.OVERTIME_100);
  }
}
